import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import GenerateDocumentButton from '../components/GenerateDocumentButton'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Allocation = {
  id?: number
  invoice: number
  invoice_number?: string
  invoice_total?: string
  invoice_amount_due?: string
  amount: string
}

type Payment = {
  id: number
  number: string
  partner: number
  partner_name: string
  payment_date: string
  bank_account: number
  bank_account_code: string
  bank_account_name: string
  amount: string
  currency_code: string
  payment_method: 'WIRE' | 'CHECK' | 'CASH' | 'CARD' | 'OTHER'
  reference: string
  notes: string
  status: 'DRAFT' | 'POSTED' | 'VOID'
  allocations: Allocation[]
  journal_entry: number | null
  journal_entry_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }
type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }
type Invoice = {
  id: number; number: string; partner: number; status: string;
  total: string; amount_paid: string; amount_due: string; currency_code: string;
}

// ─────────────────── Constants ───────────────────
const STATUS_COLOR: Record<Payment['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  POSTED: 'bg-green-100 text-green-800',
  VOID: 'bg-red-100 text-red-700',
}

const METHOD_LABEL: Record<Payment['payment_method'], string> = {
  WIRE: 'Bank transfer',
  CHECK: 'Check',
  CASH: 'Cash',
  CARD: 'Card',
  OTHER: 'Other',
}

type TabKey = 'details' | 'allocations' | 'files'

// ─────────────────── Page ───────────────────
export default function FinancePaymentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const paymentId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const paymentQ = useQuery({
    queryKey: ['finance', 'payment', paymentId],
    queryFn: async () => (await api.get<Payment>(`/finance/payments/${paymentId}/`)).data,
    enabled: !!paymentId,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })
  const invoicesQ = useQuery({
    queryKey: ['finance', 'invoices'],
    queryFn: async () => (await api.get<Invoice[]>('/finance/invoices/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Payment>) =>
      (await api.patch(`/finance/payments/${paymentId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'payment', paymentId] })
      qc.invalidateQueries({ queryKey: ['finance', 'payments'] })
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/finance/payments/${paymentId}/`),
    onSuccess: () => {
      toast.success('Payment deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'payments'] })
      navigate('/admin/finance/payments')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const postMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/finance/payments/${paymentId}/post_to_gl/`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['finance', 'payment', paymentId] })
      qc.invalidateQueries({ queryKey: ['finance', 'payments'] })
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
      qc.invalidateQueries({ queryKey: ['finance', 'journal-entries'] })
      toast.success('Posted to General Ledger')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (paymentQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (paymentQ.isError || !paymentQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Payment not found.</p>
          <Link to="/admin/finance/payments" className="text-blue-600 hover:underline">← Back to payments</Link>
        </div>
      </AdminLayout>
    )
  }

  const payment = paymentQ.data
  const customers = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'CUSTOMER' || p.partner_type === 'BOTH')
  )
  const bankAccounts = (accountsQ.data || []).filter(
    (a) => a.is_active && a.account_type === 'ASSET' && a.code.startsWith('11')
  )
  // Invoices for this payment's partner that are OPEN (have outstanding balance).
  const partnerInvoices = (invoicesQ.data || []).filter(
    (inv) => inv.partner === payment.partner && (inv.status === 'OPEN' || inv.status === 'PAID')
  )

  const locked = payment.journal_entry != null

  const save = (payload: Partial<Payment>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  // Allocation totals
  const allocatedTotal = payment.allocations.reduce((s, a) => s + Number(a.amount || 0), 0)
  const paymentTotal = Number(payment.amount)
  const unallocated = paymentTotal - allocatedTotal

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/finance/payments" className="text-muted-foreground hover:text-foreground hover:underline">← Payments</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{payment.number}</div>
            <h2 className="text-2xl font-bold text-foreground">
              {payment.partner_name || <span className="italic">No customer</span>}
            </h2>
            <div className="text-sm text-muted-foreground">
              <span>Received {payment.payment_date}</span>
              <span> · </span>
              <span className="font-mono">
                {Number(payment.amount).toFixed(2)} {payment.currency_code}
              </span>
              <span> · </span>
              <span>{METHOD_LABEL[payment.payment_method]}</span>
              <span> · </span>
              <span className="font-mono">{payment.bank_account_code} {payment.bank_account_name}</span>
              {payment.journal_entry_number && (
                <span> · Posted as <span className="font-mono">{payment.journal_entry_number}</span></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <GenerateDocumentButton
              sourceType="finance.Payment"
              sourceId={payment.id}
              label="Generate document…"
            />
            {locked ? (
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[payment.status]}`}>{payment.status}</span>
            ) : (
              <InlineStatus
                value={payment.status}
                colorMap={STATUS_COLOR}
                options={[
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'POSTED', label: 'Posted' },
                  { value: 'VOID', label: 'Void' },
                ]}
                onSave={(v) => save({ status: v })}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {payment.status === 'DRAFT' && !locked && (
                  <DropdownMenuItem
                    onClick={() => confirm(
                      `Post payment ${payment.number} to General Ledger (creates a journal entry and applies allocations to invoices)`,
                      () => postMut.mutate(),
                    )}
                    disabled={postMut.isPending}
                    className="text-green-700"
                  >
                    Post to General Ledger
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={locked}
                  onClick={() => !locked && confirm(
                    `payment ${payment.number}`,
                    () => deleteMut.mutate(),
                  )}
                  className={!locked ? 'text-destructive' : ''}
                >
                  {locked ? 'Delete (posted)' : 'Delete payment'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'allocations', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'allocations' && `Allocations (${payment.allocations.length})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          payment={payment}
          customers={customers}
          bankAccounts={bankAccounts}
          locked={locked}
          allocatedTotal={allocatedTotal}
          unallocated={unallocated}
          onSave={save}
        />
      )}
      {tab === 'allocations' && (
        <AllocationsTab
          payment={payment}
          partnerInvoices={partnerInvoices}
          locked={locked}
          unallocated={unallocated}
          onSaveAllocations={(allocs) => save({ allocations: allocs } as Partial<Payment>)}
        />
      )}
      {tab === 'files' && <FilesTab parent="finance.payment" parentId={paymentId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  payment, customers, bankAccounts, locked,
  allocatedTotal, unallocated,
  onSave,
}: {
  payment: Payment
  customers: Partner[]
  bankAccounts: Account[]
  locked: boolean
  allocatedTotal: number
  unallocated: number
  onSave: (payload: Partial<Payment>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          This payment is <strong>posted to the General Ledger</strong> ({payment.journal_entry_number}) and read-only.
          Reverse the journal entry to edit, or VOID and re-record.
        </div>
      )}

      <Section title="Payment header">
        <Row label="Number">
          <InlineText
            value={payment.number}
            onSave={(v) => onSave({ number: v })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Customer">
          <InlineSelect
            value={String(payment.partner)}
            displayLabel={payment.partner_name || <Dash />}
            options={customers.map((p) => ({
              value: String(p.id),
              label: `${p.code} ${p.name}`,
              searchText: `${p.code} ${p.name}`,
            }))}
            onSave={(v) => onSave({ partner: Number(v) })}
            disabled={locked}
            helper="Changing the customer clears existing allocations to their old invoices."
          />
        </Row>
        <Row label="Payment date">
          <InlineDate
            value={payment.payment_date}
            onSave={(v) => onSave({ payment_date: v || payment.payment_date })}
          />
        </Row>
        <Row label="Bank account">
          <InlineSelect
            value={String(payment.bank_account)}
            displayLabel={`${payment.bank_account_code} ${payment.bank_account_name}`}
            options={bankAccounts.map((a) => ({
              value: String(a.id),
              label: `${a.code} ${a.name}`,
              searchText: `${a.code} ${a.name}`,
            }))}
            onSave={(v) => onSave({ bank_account: Number(v) })}
            disabled={locked}
            helper="Cash / bank ASSET accounts (codes starting with 11)."
          />
        </Row>
      </Section>

      <Section title="Amount & method">
        <Row label="Amount">
          <InlineNumber
            value={payment.amount}
            onSave={(v) => onSave({ amount: v })}
            displayFormat={(v) => (
              <span className="font-mono">{Number(v || 0).toFixed(2)} {payment.currency_code}</span>
            )}
            disabled={locked}
          />
        </Row>
        <Row label="Currency">
          <InlineText
            value={payment.currency_code}
            onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Method">
          <InlineSelect
            value={payment.payment_method}
            displayLabel={METHOD_LABEL[payment.payment_method]}
            options={(Object.keys(METHOD_LABEL) as Payment['payment_method'][]).map((k) => ({
              value: k, label: METHOD_LABEL[k],
            }))}
            onSave={(v) => onSave({ payment_method: v as Payment['payment_method'] })}
            disabled={locked}
          />
        </Row>
        <Row label="Reference">
          <InlineText
            value={payment.reference}
            onSave={(v) => onSave({ reference: v })}
            disabled={locked}
            placeholder="Wire reference, check number, etc."
            className="font-mono"
          />
        </Row>
      </Section>

      <Section title="Allocation summary">
        <Row label="Allocated">
          <span className="font-mono">{allocatedTotal.toFixed(2)} {payment.currency_code}</span>
        </Row>
        <Row label="Unallocated">
          <span className={`font-mono ${Math.abs(unallocated) < 0.005 ? 'text-green-700' : 'text-amber-700'}`}>
            {unallocated.toFixed(2)} {payment.currency_code}
          </span>
        </Row>
        <Row label="Status">
          {Math.abs(unallocated) < 0.005
            ? <span className="text-sm text-green-700">Fully allocated.</span>
            : unallocated > 0
              ? <span className="text-sm text-amber-700">
                  Has unapplied cash. Add allocations on the Allocations tab to apply this payment to invoices.
                </span>
              : <span className="text-sm text-red-700">
                  Over-allocated by {Math.abs(unallocated).toFixed(2)}. Reduce allocations or increase payment amount.
                </span>}
        </Row>
      </Section>

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={payment.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            disabled={locked}
            placeholder="Internal notes about this payment"
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Allocations tab ───────────────────
// Allocations are nested under the payment on the backend — every edit/
// add/remove triggers a full PATCH with the whole allocations array.

function AllocationsTab({
  payment, partnerInvoices, locked, unallocated, onSaveAllocations,
}: {
  payment: Payment
  partnerInvoices: Invoice[]
  locked: boolean
  unallocated: number
  onSaveAllocations: (allocations: Allocation[]) => Promise<void>
}) {
  const { confirm, dialog } = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<{ invoice: number; amount: string }>({ invoice: 0, amount: '0' })
  const [pending, setPending] = useState(false)

  // Invoices not yet allocated to this payment.
  const allocatedInvoiceIds = new Set(payment.allocations.map((a) => a.invoice))
  const availableInvoices = partnerInvoices.filter((inv) => !allocatedInvoiceIds.has(inv.id))

  function openCreate() {
    const first = availableInvoices[0]
    setDraft({
      invoice: first?.id ?? 0,
      // Default to the lesser of remaining unallocated or invoice outstanding.
      amount: first
        ? Math.min(unallocated > 0 ? unallocated : 0, Number(first.amount_due)).toFixed(2)
        : '0',
    })
    setCreating(true)
  }

  function setDraftInvoice(invoiceId: number) {
    const inv = partnerInvoices.find((i) => i.id === invoiceId)
    setDraft({
      invoice: invoiceId,
      amount: inv
        ? Math.min(unallocated > 0 ? unallocated : 0, Number(inv.amount_due)).toFixed(2)
        : draft.amount,
    })
  }

  async function saveAllocationField(idx: number, patch: Partial<Allocation>) {
    const next = payment.allocations.map((a, i) => i === idx ? { ...a, ...patch } : a)
    await onSaveAllocations(serializeAllocations(next))
  }

  async function addAllocation() {
    if (!draft.invoice) return
    setPending(true)
    try {
      const next: Allocation[] = [
        ...payment.allocations,
        { invoice: draft.invoice, amount: draft.amount },
      ]
      await onSaveAllocations(serializeAllocations(next))
      toast.success('Allocation added')
      setCreating(false)
    } catch (e) {
      toast.error(extractErr(e))
    } finally {
      setPending(false)
    }
  }

  async function removeAllocation(idx: number) {
    const next = payment.allocations.filter((_, i) => i !== idx)
    try {
      await onSaveAllocations(serializeAllocations(next))
      toast.success('Allocation removed')
    } catch (e) {
      toast.error(extractErr(e))
    }
  }

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          {locked
            ? 'Payment posted — allocations are read-only. Reverse the journal entry to edit.'
            : `Apply this payment to outstanding invoices. ${unallocated > 0 ? `${unallocated.toFixed(2)} ${payment.currency_code} remaining to allocate.` : ''}`}
        </p>
        <Button size="sm" onClick={openCreate} disabled={locked || availableInvoices.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> Add allocation
        </Button>
      </div>

      {payment.allocations.length === 0 ? (
        <p className="text-muted-foreground text-sm">No allocations yet — this is unapplied cash.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Invoice</Th>
                <Th className="text-right">Invoice total</Th>
                <Th className="text-right">Was due</Th>
                <Th className="text-right">Applied</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {payment.allocations.map((a, i) => (
                <tr key={a.id ?? i}>
                  <Td className="font-mono text-xs">
                    {a.invoice_number ? (
                      <Link to={`/admin/finance/invoices/${a.invoice}`}
                        className="text-blue-600 hover:underline">
                        {a.invoice_number}
                      </Link>
                    ) : <span>#{a.invoice}</span>}
                  </Td>
                  <Td className="text-right font-mono text-xs text-muted-foreground">
                    {a.invoice_total ? Number(a.invoice_total).toFixed(2) : <Dash />}
                  </Td>
                  <Td className="text-right font-mono text-xs text-muted-foreground">
                    {a.invoice_amount_due ? Number(a.invoice_amount_due).toFixed(2) : <Dash />}
                  </Td>
                  <Td className="text-right">
                    <InlineNumber
                      value={a.amount}
                      step="0.01"
                      onSave={(v) => saveAllocationField(i, { amount: v })}
                      disabled={locked}
                      displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                    />
                  </Td>
                  <Td className="text-right">
                    {!locked && (
                      <RowActions
                        onEdit={() => {/* inline edits cover the row */}}
                        onDelete={() => confirm(
                          `allocation to ${a.invoice_number || `#${a.invoice}`}`,
                          () => removeAllocation(i),
                        )}
                      />
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="Add allocation"
        description="Apply part of this payment to an outstanding invoice. Defaults to the lesser of remaining unallocated cash or the invoice's outstanding amount."
        onSubmit={addAllocation}
        isPending={pending}
        submitLabel="Add allocation"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="al-invoice">Invoice *</FieldLabel>
            <SearchableSelect
              id="al-invoice"
              value={draft.invoice ? String(draft.invoice) : undefined}
              onChange={(v) => setDraftInvoice(Number(v))}
              placeholder="— select —"
              emptyLabel="No unallocated invoices for this customer"
              options={availableInvoices.map((inv) => ({
                value: String(inv.id),
                label: inv.number,
                hint: `due ${Number(inv.amount_due).toFixed(2)} ${inv.currency_code}`,
              }))}
            />
            <FieldDescription>Open invoices for {payment.partner_name} that don't yet have an allocation from this payment.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="al-amount">Amount</FieldLabel>
            <Input id="al-amount" type="number" step="0.01" value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              className="font-mono text-right" />
            <FieldDescription>Default picks the smaller of remaining unallocated cash and the invoice's outstanding amount.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// Strip computed fields the backend rejects.
function serializeAllocations(allocs: Allocation[]): Allocation[] {
  return allocs.map((a) => ({ invoice: a.invoice, amount: a.amount }))
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
